import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { IMAGES } from "@/lib/images";

export const Route = createFileRoute("/villkor")({
  head: () => ({
    meta: [
      { title: "Köp- och leveransvillkor — BBN Nordic" },
      {
        name: "description",
        content:
          "Allmänna villkor för offert, beställning, installation, garanti och betalning hos BBN Nordic AB. Konsumentköplagen och avtalslagen tillämpas.",
      },
      { property: "og:title", content: "Köp- och leveransvillkor — BBN Nordic" },
      {
        property: "og:description",
        content:
          "Offert, beställning, installation, garanti, ångerrätt och betalning — så här jobbar vi.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://bbnnordic.se/villkor" },
      { property: "og:image", content: IMAGES.hero },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/villkor" }],
  }),
  component: VillkorPage,
});

function VillkorPage() {
  return (
    <ContentPage
      eyebrow="JURIDIK"
      title="Köp- och leveransvillkor"
      subtitle="Så här jobbar vi — från offert till garanti."
      heroImage={IMAGES.installer}
      breadcrumbs={[{ label: "Villkor" }]}
      sections={[
        {
          eyebrow: "AVTALSPARTER",
          heading: "Vem du handlar av",
          paragraphs: [
            "Säljare och avtalspart är BBN Nordic AB, org.nr 556914-8306, Satellitvägen 16, 245 34 Staffanstorp. Vi innehar F-skattebevis och är auktoriserad installatör för 3M, Solar Screen och NoTrax.",
            "Dessa villkor gäller för samtliga uppdrag — privat som företag — där inget annat skriftligen avtalats. För konsumentköp tillämpas dessutom konsumentköplagen (2022:260) och konsumenttjänstlagen (1985:716).",
          ],
        },
        {
          eyebrow: "OFFERT & BESTÄLLNING",
          heading: "Från förfrågan till bekräftad order",
          paragraphs: [
            "Offert lämnas kostnadsfritt efter platsbesök eller på basis av tillhandahållna mått och bilder. Offerten är giltig i 30 dagar om inget annat anges. Pris anges inklusive material, installation, resor inom Skåne och 10 års garanti — moms tillkommer för företag, ingår för privatpersoner.",
            "Bindande avtal uppstår när du skriftligen accepterar offerten via e-post, signerat dokument eller via vårt offertsystem. Vi förbehåller oss rätten att justera priset om förutsättningarna (glastyp, åtkomlighet, mängd) skiljer sig väsentligt från offertunderlaget — sådan justering meddelas innan arbete påbörjas.",
          ],
        },
        {
          eyebrow: "INSTALLATION",
          heading: "Tid, åtkomst och färdigställande",
          paragraphs: [
            "Installationstid bestäms gemensamt och bekräftas senast 7 dagar i förväg. Kunden ansvarar för att glasytor är tillgängliga och rena, att möbler är flyttade i nödvändig omfattning och att el (220 V) finns vid arbetsplatsen.",
            "Färdigställande dokumenteras med slutbesiktning på plats. Film är fullt härdad 7–30 dagar efter montering — under denna tid är lätt fukt mellan film och glas helt normalt och försvinner av sig självt.",
          ],
        },
        {
          eyebrow: "BETALNING",
          heading: "Fakturering och villkor",
          paragraphs: [
            "Privatpersoner: faktura skickas efter färdigställande, 14 dagars betalningsvillkor. ROT-avdrag kan tillämpas där det är aktuellt — vi hanterar ansökan mot Skatteverket åt dig.",
            "Företag: 30 dagars betalningsvillkor mot faktura. Vid större projekt (>50 000 kr) kan delfakturering med 50 % vid order och 50 % vid leverans tillämpas. Dröjsmålsränta enligt räntelagen (8 % över referensräntan).",
          ],
        },
        {
          eyebrow: "GARANTI",
          heading: "10 års installations- och produktgaranti",
          paragraphs: [
            "Vi lämnar 10 års garanti på vårt installationsarbete. Garantin omfattar bubblor, kantsläpp, fellimning och optiska defekter orsakade av installationen — vi byter eller monterar om utan kostnad.",
            "3M lämnar därutöver 10–15 års produktgaranti på filmens material (delamination, missfärgning, prestandaförlust) vid auktoriserad installation. Solar Screen och NoTrax har motsvarande tillverkargarantier — se respektive datablad. Garantin gäller från färdigställandedatum och dokumenteras i slutbesiktningsprotokollet.",
            "Garantin täcker inte skador orsakade av yttre våld, felaktig rengöring (slipande medel, ammoniak på filmsidan), ombyggnation eller normalt slitage.",
          ],
        },
        {
          eyebrow: "ÅNGERRÄTT",
          heading: "Konsumentens ångerrätt",
          paragraphs: [
            "Distansköp (offert + accept via e-post utan platsbesök) ger 14 dagars ångerrätt enligt distansavtalslagen (2005:59). Ångerrätten upphör när installationen påbörjats med ditt uttryckliga samtycke — film är en specialanpassad produkt som kapas efter ditt glas och kan inte återanvändas.",
            "Vid platsbesök i hemmet gäller motsvarande 14 dagars ångerrätt enligt lagen om distansavtal och avtal utanför affärslokal.",
          ],
        },
        {
          eyebrow: "REKLAMATION",
          heading: "Om något inte blev rätt",
          paragraphs: [
            "Reklamation görs skriftligen till info@bbnnordic.se eller 0771 502 502 så snart felet upptäcks, dock senast inom skälig tid (2 månader anses alltid skäligt för konsument).",
            "Vi åtgärdar berättigade reklamationer kostnadsfritt — i första hand genom omarbete, i andra hand genom prisavdrag eller hävning. Tvist som inte kan lösas i samförstånd kan av konsument hänskjutas till Allmänna reklamationsnämnden (ARN).",
          ],
        },
        {
          eyebrow: "ANSVAR",
          heading: "Skador och försäkring",
          paragraphs: [
            "BBN Nordic AB har ansvarsförsäkring som täcker skador på kundens egendom orsakade av oss under installationen. Anmälan sker direkt till oss för vidare hantering.",
            "Vårt ansvar är begränsat till direkt skada upp till orderns värde. Indirekta skador (utebliven vinst, driftstopp) ersätts inte — företag rekommenderas att teckna egen verksamhetsförsäkring som täcker sådana risker.",
          ],
        },
        {
          eyebrow: "TILLÄMPLIG LAG",
          heading: "Lag och tvistlösning",
          paragraphs: [
            "Svensk lag tillämpas på avtalet. Tvist avgörs i första hand genom dialog, i andra hand av Allmänna reklamationsnämnden (för konsument) eller allmän domstol med Malmö tingsrätt som första instans.",
            "Senast uppdaterad: 2026-06-17. Vi förbehåller oss rätten att uppdatera villkoren — den version som var publicerad vid orderns ingående gäller för det specifika avtalet.",
          ],
        },
      ]}
    />
  );
}
